import { ClipboardEvent, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useTeamsStore, useUserDisplayNames } from "@/store/teamsStore";
import { useOnboardingStore } from "@/modules/onboarding/onboardingStore";
import { parseTaskPasteLines } from "@/modules/onboarding/taskPaste";
import { TaskDescriptionSheet } from "./TaskDescriptionSheet";
import { TaskEditorRow } from "./TaskEditorRow";

/**
 * Board task editor: inline rows with reordering, an add-task input (Enter
 * to add), and multi-line paste that bulk-creates tasks from markdown
 * checklist lines with trailing `@email` assignees.
 */
export function TasksEditorSection() {
  const taskMap = useOnboardingStore((state) => state.taskMap);
  const taskCreateState = useOnboardingStore((state) => state.taskCreateState);
  const createTask = useOnboardingStore((state) => state.createTask);
  const reorderTasks = useOnboardingStore((state) => state.reorderTasks);
  const users = useTeamsStore((state) => state.users);
  useUserDisplayNames();

  const [newTaskName, setNewTaskName] = useState("");
  const [descriptionTaskId, setDescriptionTaskId] = useState<string | null>(
    null,
  );

  const tasks = useMemo(
    () => [...taskMap.values()].sort((a, b) => a.order - b.order),
    [taskMap],
  );

  const nextOrder = () =>
    tasks.reduce((max, task) => Math.max(max, task.order), 0) + 1;

  const handleAdd = async () => {
    const name = newTaskName.trim();
    if (!name) {
      return;
    }
    const created = await createTask({ name, order: nextOrder() });
    if (created) {
      setNewTaskName("");
    }
  };

  const handlePaste = async (event: ClipboardEvent<HTMLInputElement>) => {
    const text = event.clipboardData.getData("text");
    if (!text.includes("\n")) {
      return;
    }
    event.preventDefault();
    const lines = parseTaskPasteLines(text);
    const emailToUserId = new Map(
      users.map((user) => [user.email.toLowerCase(), user.id]),
    );
    const startOrder = nextOrder();
    for (const [index, line] of lines.entries()) {
      await createTask({
        name: line.name,
        order: startOrder + index,
        assigneeUserId: line.assigneeEmail
          ? (emailToUserId.get(line.assigneeEmail) ?? null)
          : null,
      });
    }
  };

  const handleMove = (index: number, direction: -1 | 1) => {
    const current = tasks[index];
    const other = tasks[index + direction];
    if (!other) {
      return;
    }
    reorderTasks([
      { id: current.id, order: other.order },
      { id: other.id, order: current.order },
    ]);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Tasks</CardTitle>
        <CardDescription>
          The checklist the team works through. Paste a multi-line list (e.g.
          &quot;- [ ] Task @email&quot;) to bulk-add.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {tasks.map((task, index) => (
          <TaskEditorRow
            key={task.id}
            task={task}
            users={users}
            isFirst={index === 0}
            isLast={index === tasks.length - 1}
            onMoveUp={() => handleMove(index, -1)}
            onMoveDown={() => handleMove(index, 1)}
            onEditDescription={() => setDescriptionTaskId(task.id)}
          />
        ))}
        <TaskDescriptionSheet
          task={
            descriptionTaskId ? (taskMap.get(descriptionTaskId) ?? null) : null
          }
          onClose={() => setDescriptionTaskId(null)}
        />
        <div className="flex items-center gap-3 pt-1">
          <Input
            placeholder="Add a task — press Enter, or paste a list"
            value={newTaskName}
            onChange={(event) => setNewTaskName(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                handleAdd();
              }
            }}
            onPaste={handlePaste}
            disabled={taskCreateState.isSaving}
          />
          {taskCreateState.isSaving && (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-muted-foreground" />
          )}
          {taskCreateState.error && (
            <span className="shrink-0 text-sm text-destructive">
              {taskCreateState.error}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
